import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdsCampaign } from 'src/app/classes/ads-campaign';
import { AdsCampaignArea } from 'src/app/classes/ads-campaign-area';
import { AdsCampaignAreaService } from 'src/app/services/ads-campaign-area.service';

import * as L from 'leaflet';
import { Organization } from 'src/app/classes/organization';
import { PlaceService } from 'src/app/services/place.service';

/** Theme primary (--bs-primary); translucent fill for target area */
const RADIUS_CIRCLE_STYLE: L.PathOptions = {
  color: '#375a7f',
  weight: 2,
  opacity: 0.9,
  fillColor: '#375a7f',
  fillOpacity: 0.22,
};

@Component({
  selector: 'app-area',
  templateUrl: './area.component.html',
  styleUrls: ['./area.component.css']
})
export class AreaComponent implements OnInit, AfterViewInit, OnDestroy {

  adsCampaign: AdsCampaign = new AdsCampaign()
  adsCampaignArea: AdsCampaignArea = new AdsCampaignArea()
  organization: Organization

  map!: L.Map;

  private radiusCircle?: L.Circle;
  private readonly destroyed$ = new Subject<void>();

  cities: { city: string, placeId: string }[] = []

  form = new FormGroup({
    city: new FormControl('', [Validators.required]),
    lat: new FormControl(null, [Validators.required]),
    long: new FormControl(null, [Validators.required]),
    radius: new FormControl(20, [Validators.required]),
  })

  constructor(
    public activeModal: NgbActiveModal,
    private adsCampaignAreaService: AdsCampaignAreaService,
    private placeService: PlaceService
  ) { }

  ngOnInit(): void {
    this.map = L.map('map')

    L.tileLayer(this.organization.osm_tile_url, { maxZoom: 18, attribution: '' }).addTo(this.map)

    this.form.get('radius')!.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => this.syncRadiusCircle())

    if (this.adsCampaignArea.id) {
      this.adsCampaignAreaService.get(this.adsCampaign.id, this.adsCampaignArea.id).subscribe(adsCampaignArea => {
        this.adsCampaignArea = adsCampaignArea
        this.form.patchValue({ ...adsCampaignArea })
        this.map.setView([Number(adsCampaignArea.lat), Number(adsCampaignArea.long)], 11)
        this.map.invalidateSize()
        this.syncRadiusCircle()
      })
    }

    else {
      // Show the whole country from organization.country_area JSON
      const bounds = L.latLngBounds(
        JSON.parse(this.organization.country_area)
      )

      this.map.fitBounds(bounds)
    }
  }

  ngAfterViewInit(): void {
    // Map is inside a modal: Leaflet often measures 0×0 until the modal has finished laying out.
    setTimeout(() => {
      this.map.invalidateSize()
      this.syncRadiusCircle()
    }, 150)
  }

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
    if (this.radiusCircle) {
      this.map.removeLayer(this.radiusCircle)
      this.radiusCircle = undefined
    }
  }

  /** API may use `long` or `lng` for longitude. */
  private normalizeLng(row: { long?: number | null; lng?: number | null }): number {
    const v = row.long ?? row.lng
    return v != null ? Number(v) : NaN
  }

  /** True when we have real coordinates (not default 0,0 before a city is chosen). */
  private hasCityLocation(): boolean {
    const lat = Number(this.form.get('lat')!.value)
    const long = Number(this.form.get('long')!.value)
    if (!Number.isFinite(lat) || !Number.isFinite(long)) {
      return false
    }
    return !(lat === 0 && long === 0)
  }

  private syncRadiusCircle(): void {
    if (!this.hasCityLocation()) {
      if (this.radiusCircle) {
        this.map.removeLayer(this.radiusCircle)
        this.radiusCircle = undefined
      }
      return
    }

    const lat = Number(this.form.get('lat')!.value)
    const long = Number(this.form.get('long')!.value)
    const radiusKm = Number(this.form.get('radius')!.value)
    if (!Number.isFinite(lat) || !Number.isFinite(long) || !Number.isFinite(radiusKm) || radiusKm <= 0) {
      return
    }

    const radiusM = radiusKm * 1000
    const center: L.LatLngExpression = [lat, long]

    if (this.radiusCircle) {
      this.radiusCircle.setLatLng(center)
      this.radiusCircle.setRadius(radiusM)
    } else {
      this.radiusCircle = L.circle(center, { ...RADIUS_CIRCLE_STYLE, radius: radiusM }).addTo(this.map)
    }

    this.fitMapToRadiusCircle()
  }

  private fitMapToRadiusCircle(): void {
    if (!this.radiusCircle) {
      return
    }
    this.map.fitBounds(this.radiusCircle.getBounds(), {
      padding: [32, 32],
      maxZoom: 18,
      animate: false,
    })
  }

  updateCity(event: Event) {
    const query = (event.target as HTMLInputElement).value

    this.placeService.getCities(query, this.organization.id).subscribe(cities => {
      this.cities = cities
    })
  }

  selectCity(city: { city: string, placeId: string }) {
    this.placeService.place(city.placeId).subscribe(place => {
      const long = this.normalizeLng(place)
      this.form.patchValue({
        city: place.city,
        lat: place.lat,
        long
      })

      this.map.invalidateSize()
      this.syncRadiusCircle()

      this.cities = []
    })
    
  }

  save() {
    this.adsCampaignAreaService.save(this.adsCampaign.id, this.adsCampaignArea.id, this.form.value as AdsCampaignArea).subscribe(adsCampaignArea => {
      this.activeModal.close(this.form.value)
    })
  }
}
